/**
 * Fait : la partie de l'écran de l'argent réservée au parent administrateur —
 *   le plafond de la famille entière, puis un TABLEAU qui résume qui dispose de
 *   quoi, avec un bouton par ligne qui ouvre le réglage de cette personne-là.
 *
 *   POURQUOI UN TABLEAU, écrit le 2026-08-26 : la première version dépliait le
 *   formulaire complet de CHAQUE personne, l'un sous l'autre. Charles, en s'en
 *   servant : « déjà avec deux noms ça prend une page complète ». À dix
 *   personnes c'était illisible, et rien ne permettait de comparer d'un coup
 *   d'œil qui avait combien — ce qui est précisément la question qu'on se pose
 *   devant cet écran. Ses mots pour le remède : « un tableau qui résume qui
 *   l'argent et un bouton qui ouvre un espace formulaire pour régler le niveau
 *   de dépense pour chaque utilisateur ».
 *
 *   LES DEUX LISTES SONT CROISÉES ICI, et il le faut : le serveur ne rend que
 *   les budgets DÉJÀ POSÉS. Une personne à qui l'on n'a jamais rien accordé
 *   n'apparaîtrait donc nulle part — et c'est justement celle à qui l'on veut
 *   ouvrir un budget. La famille donne les gens, les budgets donnent les
 *   chiffres, et qui n'a pas de budget est montré à zéro, dit comme tel.
 *
 * Ne fait pas : elle n'appelle pas le serveur — l'écran qui la contient tient
 *   l'état et recharge — et ne cache rien par droit : un membre ordinaire ne
 *   reçoit tout simplement pas le tableau, et la route lui refuse les plafonds.
 */

import { useState } from 'react';

import { Partie } from '../gabarits/Partie.tsx';
import type { CompteDeLaFamille } from './api.ts';
import type { BudgetsVus, BudgetVu } from './api-argent.ts';
import { FormulaireDePlafond } from './FormulaireDePlafond.tsx';
import { enEuros } from './montants.ts';

/** Le budget de qui n'en a jamais eu : tout à zéro, et jamais posé. */
const JAMAIS_POSE: BudgetVu = {
  plafondCentimes: 0,
  engageCentimes: 0,
  consommeCentimes: 0,
  version: 0,
  pose: false,
};

/** Ce qui reste sur un budget, jamais négatif. */
function reste(budget: BudgetVu): number {
  return Math.max(0, budget.plafondCentimes - budget.consommeCentimes - budget.engageCentimes);
}

export function SectionDesPlafonds({
  budgets,
  famille,
  enCours,
  surPlafondGlobal,
  surPlafondDeMembre,
}: {
  readonly budgets: BudgetsVus;
  readonly famille: readonly CompteDeLaFamille[];

  /** La cible dont l'enregistrement est en vol, pour n'éteindre que son bouton. */
  readonly enCours: string | null;

  readonly surPlafondGlobal: (plafondCentimes: number, version: number) => void;
  readonly surPlafondDeMembre: (compteId: string, plafondCentimes: number, version: number) => void;
}) {
  /** La personne dont on règle le plafond, s'il y en a une. */
  const [regle, setRegle] = useState<string | null>(null);

  const parCompte = new Map((budgets.tableau ?? []).map((ligne) => [ligne.compteId, ligne]));
  const budgetDe = (compteId: string) => parCompte.get(compteId) ?? JAMAIS_POSE;
  const choisie = famille.find((compte) => compte.id === regle);

  return (
    <>
      <Partie
        etiquette="Famille"
        titre="Le plafond de toute la famille"
        nature="partage"
        niveauDeTitre={3}
        aide={
          'Ce plafond borne les dépenses de tout le monde réunies. Il s’applique en plus ' +
          'des plafonds individuels : c’est toujours le plus bas des deux qui arrête.'
        }
        data-role="partie-plafond-global"
      >
        <FormulaireDePlafond
          /* LA CLÉ PORTE LA VERSION : après un enregistrement, le formulaire est
             remonté avec le montant que le serveur a réellement retenu. Sans
             elle, il garderait la valeur tapée même quand le serveur a refusé —
             et l'écran mentirait sur l'état du budget. */
          key={`global-${budgets.global.version}`}
          libelle="Plafond de la famille"
          budget={budgets.global}
          enCours={enCours === 'global'}
          marque="plafond-global"
          surEnvoi={surPlafondGlobal}
        />
      </Partie>

      <Partie
        etiquette="Chacun"
        titre="Qui peut dépenser quoi"
        nature="gens"
        niveauDeTitre={3}
        aide="Le tableau résume la situation de chacun. « Régler » ouvre le formulaire d’une personne."
        data-role="partie-plafonds-des-membres"
      >
        {famille.length === 0 ? (
          <p data-etat="vide" data-role="aucun-membre">
            La famille ne compte encore aucun autre compte.
          </p>
        ) : (
          /* LE TABLEAU DÉFILE DANS SA PROPRE BOÎTE. À fort grossissement quatre
             colonnes ne tiennent pas dans la largeur : sans ce conteneur, c'est
             la PAGE ENTIÈRE qui se met à glisser de gauche à droite, et l'on
             perd le fil de lecture partout, pas seulement ici. */
          <div className="tableau-qui-defile">
            <table data-role="tableau-des-plafonds">
              <caption className="pour-lecteur-decran">
                Plafond, dépenses et reste disponible pour chaque personne de la famille
              </caption>
              <thead>
                <tr>
                  <th scope="col">Personne</th>
                  <th scope="col">Plafond</th>
                  <th scope="col">Dépensé</th>
                  <th scope="col">Reste</th>
                  <th scope="col">
                    <span className="pour-lecteur-decran">Régler le plafond</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {famille.map((compte) => {
                  const budget = budgetDe(compte.id);

                  return (
                    <tr key={compte.id} data-role="ligne-de-plafond" data-compte={compte.id}>
                      <th scope="row">
                        {compte.nomAffiche}
                        {compte.actif ? '' : ' — accès fermé'}
                      </th>
                      <td>{budget.pose ? enEuros(budget.plafondCentimes) : 'aucun'}</td>
                      <td>{enEuros(budget.consommeCentimes)}</td>
                      <td>{budget.pose ? enEuros(reste(budget)) : 'rien'}</td>
                      <td>
                        <button
                          type="button"
                          data-role="regler-le-plafond"
                          data-compte={compte.id}
                          onClick={() => setRegle(regle === compte.id ? null : compte.id)}
                        >
                          {regle === compte.id ? 'Fermer' : 'Régler'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {choisie !== undefined && (
          <div data-role="reglage-d-un-membre" data-compte={choisie.id}>
            <h4>Le plafond de {choisie.nomAffiche}</h4>

            <FormulaireDePlafond
              key={`${choisie.id}-${budgetDe(choisie.id).version}`}
              libelle={`Plafond de ${choisie.nomAffiche}`}
              budget={budgetDe(choisie.id)}
              enCours={enCours === choisie.id}
              marque={`plafond-${choisie.id}`}
              surEnvoi={(plafondCentimes, version) => {
                surPlafondDeMembre(choisie.id, plafondCentimes, version);
              }}
            />
          </div>
        )}
      </Partie>
    </>
  );
}
